/**
 * 甘特图里程碑标记组件
 *
 * 在时间轴上显示里程碑的菱形标记
 */

import { Diamond } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

import type { GanttMilestone } from './types'

interface MilestoneMarkerProps {
  /** 里程碑数据 */
  milestone: GanttMilestone
  /** 水平位置 (px) */
  position: number
  /** 点击回调 */
  onClick: () => void
}

export function MilestoneMarker({
  milestone,
  position,
  onClick,
}: MilestoneMarkerProps) {
  const [isHovered, setIsHovered] = React.useState(false)

  return (
    <div
      className="absolute top-0 z-30"
      style={{ left: position - 12 }}
    >
      {/* 里程碑图标 */}
      <button
        type="button"
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded transition-transform hover:scale-110',
          milestone.isOverdue
            ? 'text-red-500'
            : milestone.status === 'closed'
              ? 'text-green-500'
              : 'text-amber-500'
        )}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Diamond
          className={cn(
            'h-5 w-5',
            milestone.status === 'closed' && 'fill-current'
          )}
        />
      </button>

      {/* Tooltip */}
      {isHovered && (
        <MilestoneTooltip milestone={milestone} />
      )}
    </div>
  )
}

// ==================== 子组件 ====================

interface MilestoneTooltipProps {
  milestone: GanttMilestone
}

function MilestoneTooltip({ milestone }: MilestoneTooltipProps) {
  return (
    <div className="absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-popover px-3 py-2 text-sm shadow-lg">
      <div className="flex items-center gap-2">
        <Diamond
          className={cn(
            'h-4 w-4',
            milestone.isOverdue
              ? 'text-red-500'
              : milestone.status === 'closed'
                ? 'text-green-500'
                : 'text-amber-500',
            milestone.status === 'closed' && 'fill-current'
          )}
        />
        <span className="font-medium">{milestone.title}</span>
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        <div>截止日期: {milestone.dueDate}</div>
        <div>进度: {milestone.progress}%</div>
        <div>
          状态:{' '}
          {milestone.status === 'closed' ? (
            <span className="text-green-500">已完成</span>
          ) : milestone.isOverdue ? (
            <span className="text-red-500">已逾期</span>
          ) : (
            <span className="text-amber-500">进行中</span>
          )}
        </div>
      </div>
      {/* Tooltip 箭头 */}
      <div className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-popover" />
    </div>
  )
}
